<template>
  <div>
    <authorisation-wrapper
      :resource="ACCOUNTS"
      :action="WRITE"
      v-slot="{ isAllowed: allowedWriteAccounts }"
    >
      <cl-button
        :variant="'secondary'"
        type="button"
        :disabled="loading"
        :readonly="!allowedWriteAccounts"
        data-test-id="msp-customers-data-table-add-btn"
        @on-click="modalVisible = true"
      >
        {{ t("add-customer-button") }}
      </cl-button>
    </authorisation-wrapper>
    <t-add-customers-index
      v-if="modalVisible"
      :visible="modalVisible"
      :list-all-licences="listOfMspLicences"
      :msp-obj="mspOrganisationObj"
      @on-create="customerCreated"
      @on-close="onModalClose"
    />
  </div>
</template>

<script setup lang="ts">
import TAddCustomersIndex from "@/modals/t-add-customers/t-add-customers.index.vue";
import { onMounted, ref, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import { Organisation } from "@/types/organisatio.ts";
import { useRoute, useRouter } from "vue-router";
import { useOrganisationsStore } from "@/store/organisations.store";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";
import { ResellerOrganisationResponse } from "@/types/licenceSolutions.ts";

import {
  useAuthorisation,
  AuthorisationWrapper,
  ACCOUNTS,
  WRITE,
} from "@/authorisation";

const listOfMspLicences = ref<ResellerOrganisationResponse>(
  {} as ResellerOrganisationResponse,
);
const mspOrganisationObj = ref<Organisation>({} as Organisation);

const { t } = useI18n();

interface Props {
  selectedMspUuid: string;
  loading: boolean;
}

const props = defineProps<Props>();
const { selectedMspUuid, loading } = toRefs(props);

const emit = defineEmits(["created"]);

const modalVisible = ref<boolean>(false);

const route = useRoute();
const router = useRouter();

const { hasPermission } = useAuthorisation();
const organisationsStore = useOrganisationsStore();
const licenceStore = useLicenceStore();

onMounted(async () => {
  try {
    listOfMspLicences.value = (await licenceStore.listAllLicences({
      uuid: selectedMspUuid.value,
    })) as ResellerOrganisationResponse;
    const response = (await organisationsStore.showOrg({
      uuid: selectedMspUuid.value,
    }))!;
    mspOrganisationObj.value = response.data;
  } catch {
    // stub
  }

  if (!hasPermission(WRITE, ACCOUNTS)) {
    return;
  }

  if (route.query.openModal) {
    modalVisible.value = true;
  }
});

const onModalClose = () => {
  modalVisible.value = false;
  const newQuery = { ...route.query };
  delete newQuery.openModal;
  router.replace({ query: newQuery });
};

const customerCreated = () => {
  modalVisible.value = false;
  emit("created");
};
</script>
